import EditorActions from 'components/Common/EditorActions'
import React, { useState } from "react"
import DateTimeField from "components/Common/DateTimeField"
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"
import FileUpload from "components/Common/FileUpload"
import {
  PARTNER_LOGO_PATH,
  PARTNER_MARKETS,
  PARTNER_TIERS,
  marketLabel,
  partnerFormErrors,
  partnerFormValues,
  partnerLogoFilename,
} from "./partnerPayload"

// Add or edit one partner. Two fields carry the public behaviour and are required even though the
// API would accept a partner without them: the logo, which is the plate, and the website, which is
// what a click on the plate opens.
//
// A modal rather than a card under the band: the band is tall enough at real logo sizes that a form
// opening below it lands off-screen, and "click a plate to edit it" has to put the editor where the
// click was. It is also how the rest of CP edits a record.

const labelStyle = { fontSize: 12, fontWeight: 600 }
const hintStyle = { fontSize: 11 }

const PartnerForm = ({ partner, defaultTier, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(() => partnerFormValues(partner, defaultTier))
  const [initialForm] = useState(form)
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm)
  const [errors, setErrors] = useState([])

  // Nothing is marked until the first attempt to save — but once something is marked, the marks have
  // to keep up. Leaving "a logo is required" under a logo that has just been uploaded reads as a
  // form that has stopped listening.
  const setField = (field, value) =>
    setForm(current => {
      const next = { ...current, [field]: value }
      setErrors(marked => (marked.length ? partnerFormErrors(next) : marked))
      return next
    })

  const invalid = field => errors.includes(field)
  const editing = Boolean(form.id)

  const submit = () => {
    const formErrors = partnerFormErrors(form)
    setErrors(formErrors)
    if (!formErrors.length) onSave(form)
  }

  // The page mounts this only while a partner is being edited, so it is open by definition. Closing
  // is the page's business — the backdrop, the ×, and Cancel all mean the same thing.
  return (
    <Modal isOpen scrollable size="xl" toggle={onCancel}>
      <ModalHeader toggle={onCancel}>
        {editing ? `Edit ${partner.name}` : "Add partner"}
      </ModalHeader>

      <ModalBody>
        <Row>
          <Col md={4}>
            <Label style={labelStyle}>Logo</Label>
            <FileUpload
              url={PARTNER_LOGO_PATH}
              defaultPhoto={partnerLogoFilename(form.logoUrl)}
              onChange={filename => setField("logoFilename", filename)}
            />
            {invalid("logo") && <div className="text-danger mt-1">A logo is required.</div>}
            <p className="text-muted mt-2 mb-0" style={hintStyle}>
              Square PNG, transparent background, at least 300×300. Every plate renders at the same
              size, so a non-square logo gets letterboxed rather than cropped.
            </p>
          </Col>

          <Col md={8}>
            <div className="mb-3">
              <Label for="partner-name" style={labelStyle}>
                Name
              </Label>
              <Input
                id="partner-name"
                bsSize="sm"
                placeholder="Doctor Detergent"
                value={form.name}
                invalid={invalid("name")}
                onChange={event => setField("name", event.target.value)}
              />
              <span className="text-muted" style={hintStyle}>
                Used as the image alt text and the link&apos;s accessible name.
              </span>
            </div>

            <div className="mb-3">
              <Label for="partner-website" style={labelStyle}>
                Website
              </Label>
              <Input
                id="partner-website"
                bsSize="sm"
                placeholder="doctordetergent.ro"
                value={form.website}
                invalid={invalid("website")}
                onChange={event => setField("website", event.target.value)}
              />
              <span className="text-muted" style={hintStyle}>
                Clicking the logo opens this in a new tab.
              </span>
            </div>

            <Row>
              <Col md={6} className="mb-3">
                <Label for="partner-tier" style={labelStyle}>
                  Tier
                </Label>
                <Input
                  id="partner-tier"
                  type="select"
                  bsSize="sm"
                  value={form.tier}
                  invalid={invalid("tier")}
                  onChange={event => setField("tier", event.target.value)}
                >
                  {PARTNER_TIERS.map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md={6} className="mb-3">
                <Label for="partner-ends-at" style={labelStyle}>
                  Contract ends <span className="text-muted">(optional)</span>
                </Label>
                <DateTimeField
                  id="partner-ends-at"
                  mode="date"
                  bsSize="sm"
                  value={form.endsAt}
                  onChange={event => setField("endsAt", event.target.value)}
                />
                <span className="text-muted" style={hintStyle}>
                  Takes the partner off the band the day after this date. Leave it empty for an
                  open-ended contract.
                </span>
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Label for="partner-market" style={labelStyle}>
                  Markets
                </Label>
                <Input
                  id="partner-market"
                  type="select"
                  bsSize="sm"
                  value={form.market}
                  onChange={event => setField("market", event.target.value)}
                >
                  {PARTNER_MARKETS.map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                  {/* Whatever an earlier write stored, kept selectable so saving preserves it. */}
                  {form.market === "custom" && (
                    <option value="custom">{marketLabel(form.countries)}</option>
                  )}
                </Input>
              </Col>

              <Col md={6} className="mb-3 d-flex align-items-end">
                <Label check style={{ fontSize: 13 }}>
                  <Input
                    type="checkbox"
                    className="mr-2"
                    checked={form.active}
                    onChange={event => setField("active", event.target.checked)}
                  />
                  Show on the band
                </Label>
              </Col>
            </Row>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter><EditorActions dirty={dirty} pending={saving} saveLabel="Save">
        <Button color="secondary" size="sm" outline disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          className="d-inline-flex align-items-center"
          disabled={saving}
          onClick={submit}
        >
          Save
          {saving && <Spinner className="ml-2" size="sm" animation="grow" variant="dark" />}
        </Button>
      </EditorActions></ModalFooter>
    </Modal>
  )
}

export default PartnerForm
